import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { useStyletron } from '@thanx/uikit/theme'
import { buildTranslate } from 'locales'
import React from 'react'

const t = buildTranslate('live_updates')
const tErrors = buildTranslate('live_updates.errors')

export interface DetailStatusPlaceholderProps {
  /** 'loading' shows a spinner (the fetch is still catching up to a
   * switch); 'unavailable' shows a retry notice (the fetch errored, or
   * loaded but can't report a numeric version — either way it will never
   * resolve into real content). The two states are mutually exclusive by
   * construction in every caller. */
  state: 'loading' | 'unavailable'
  /** Distinguishes this notice's Retry button from any other rendered
   * alongside it — the sections and preview slots on the Configuration tab
   * can both show one at once, so a bare "Retry" label would leave a
   * screen-reader user unable to tell them apart. Required even where only
   * one instance renders today, so a future addition can't silently
   * reintroduce the ambiguity. */
  retryAriaLabel: string
  onRetry: () => void
  'data-testid': string
  /** The rail's loading spinner sits in a slightly shorter card than the
   * sections/preview columns' (32px vs 48px vertical padding). */
  loadingPadding?: string
}

// Shared by the three slots that stand in for real content while the
// SELECTED configuration's own detail hasn't resolved yet (Sandbox QA):
// ProviderStatusSection's rail, and ConfigurationTab's status-sections
// column and sticky preview column. Kept as one component so the two
// states' markup can't drift between the three call sites.
const DetailStatusPlaceholder = ({
  state,
  retryAriaLabel,
  onRetry,
  loadingPadding = '48px 0',
  'data-testid': testId,
}: DetailStatusPlaceholderProps) => {
  const [css] = useStyletron()

  if (state === 'loading') {
    return (
      <div
        role="status"
        data-testid={testId}
        className={css({ textAlign: 'center', padding: loadingPadding })}
      >
        {/* Inline spin markup rather than the shared Spinner component: two
            of these can render at once (ConfigurationTab's sections and
            preview slots both loading together), and Spinner always emits
            its OWN data-testid="Loading" internally — which would collide
            with itself across the two simultaneous instances, and with the
            page-level <Loading/> component's identical testid. */}
        <span
          aria-hidden="true"
          className="fa fa-spin fa-circle-o-notch fa-2x"
        />
        <span className="sr-only">{t('loading')}</span>
      </div>
    )
  }

  return (
    <div data-testid={testId}>
      <Notification kind="negative" size="full">
        {tErrors('load_failed')}
      </Notification>
      <Button className="mt-m" aria-label={retryAriaLabel} onClick={onRetry}>
        {tErrors('retry')}
      </Button>
    </div>
  )
}

export default DetailStatusPlaceholder
